import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { RatingService } from '../../services/rating.service';
import { LocaleService } from '../../services/locale.service';
import { FormBuilder, Validators } from '@angular/forms';
import { CustomQuestion } from '../../models/custom-question.model';
import { Subject } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { Doctor } from '../../models/doctor.model';
import { PatientService } from '../../services/patient.service';
import { defaultQuestionsValidator } from '../../utils/validators';
import { AmplitudeService } from '../../services/amplitude.service';
import { AmplitudeEvents } from '../../constants/amplitude-events';
import { Call } from '../../models/call.model';
import { Patient } from '../../models/patient.model';
import { CallService } from 'src/app/services/call.service';
import { CallRateRequest } from 'src/app/models/call-rate-request.model';
import { MessageNavigationService } from 'src/app/services/message-navigation.service';

@Component({
  selector: 'app-rating-call',
  templateUrl: './rating-call.component.html',
  styleUrls: ['./rating-call.component.css'],
  standalone: false
})
export class RatingCallComponent implements OnInit, OnDestroy {
  private readonly ratingService = inject(RatingService);
  private readonly localeService = inject(LocaleService);
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly patientService = inject(PatientService);
  private readonly callService = inject(CallService);
  private readonly amplitudeService = inject(AmplitudeService);
  private readonly messageNavigationService = inject(MessageNavigationService);

  public questions: CustomQuestion[] = [];
  public stars: { [key: string]: number[] } = {};
  public isFavoriteDoctor = false;
  public isLoading = true;
  private hasCustomQuestions = false;
  private destroy$ = new Subject<void>();

  public call: Call;
  public patient: Patient;
  public doctor: Doctor;

  formGroup = this.fb.group({});

  ngOnInit(): void {
    this.getParams();
  }

  async getParams(): Promise<void> {
    const { call } = this.route.snapshot.queryParams;

    if (!call) {
      this.goToMessage('rating_cannot_rate_call');
      return;
    }

    this.getCall(call);
  }

  private async getCall(callId: string): Promise<void> {
    try {
      this.call = await this.callService.getOne(callId, []);
      if (await this.isCallRated(callId)) {
        return;
      }
      await this.setData();
      await this.loadQuestions();
      this.isLoading = false;
    } catch (error) {
      this.goToMessage(error?.error?.message || 'rating_cannot_rate_call');
    }
  }

  async setData(): Promise<void> {
    this.patient = this.call.patient;
    this.doctor = this.call.doctor;
    this.isFavoriteDoctor = await this.checkIfDoctorIsFavorite();
    this.hasCustomQuestions = this.call.provider.supportsCustomQuestions;
  }

  private async checkIfDoctorIsFavorite(): Promise<boolean> {
    if (!this.patient.id) {
      return false;
    }
    const favoritedDoctors = await this.patientService.getFavoritedDoctors(this.patient.id);
    return favoritedDoctors.some((favoritedDoctor: Doctor) =>
      this.doctor ? this.doctor.id === favoritedDoctor.id : false
    );
  }

  async isCallRated(callId: string): Promise<boolean> {
    try {
      const isRated = await this.ratingService.isCallRated(callId);
      if (isRated) {
        this.goToMessage('rating_call_already_rated');
        return true;
      }
    } catch (error) {
      if (error?.error?.status === 401) {
        this.goToMessage('rating_time_limit_exceeded');
        return true;
      }
      this.goToMessage(error?.error?.message);
      return true;
    }

    return false;
  }

  private async loadQuestions(): Promise<void> {
    const questions = this.hasCustomQuestions
      ? await this.ratingService.getSatisfactorySurveyQuestions(this.call.provider.id)
      : (await this.ratingService.getDefaultCustomQuestions()).map((q) => ({ ...q, question: this.localeService.getString(q.question) }));

    this.questions = questions.sort((a, b) => a.order - b.order);

    this.questions.forEach(q => {
      if (q.type === 'number') {
        this.stars[q.id] = Array.from({ length: q.rating || 5 });
      }
    });

    this.buildFormControls();
    this.formGroup.setValidators(this.hasCustomQuestions ? [] : [defaultQuestionsValidator()]);
  }

  private buildFormControls(): void {
    this.questions.forEach((q) => {
      const validators = this.hasCustomQuestions && q.type === 'number' ? [Validators.required] : [];
      this.formGroup.addControl(q.id, this.fb.control(null, validators));
    });
  }

  setRate(controlId: string, value: number): void {
    this.formGroup.get(controlId)?.setValue(value);
  }

  onBooleanQuestionClick(questionId: string, value: boolean): void {
    const control = this.formGroup?.get(questionId);
    if (!control) return;
    control.setValue(value);
  }

  async submit(): Promise<void> {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }
    try {
      let callRateRequest: CallRateRequest = this.formGroup.value;
      if (this.hasCustomQuestions) {
        callRateRequest = {
          questions: [],
          answers: [],
          call: this.call.id,
        };
        this.questions.forEach((question) => {
          callRateRequest.questions!.push(question.question);
          callRateRequest.answers!.push(this.formGroup.get(question.id)!.value);
        });
      }

      await this.ratingService.rateCall(callRateRequest, this.call.id, true);
      this.amplitudeService.logEvent(AmplitudeEvents.PATIENT_CALL_QUALIFY);
      this.goToMessage('rating_thank_you_service_feedback');
    } catch (error) {
      if (error.error.type === 'cannotRateCall') {
        this.goToMessage('rating_call_already_rated');
        return;
      }
      this.goToMessage(error?.error?.message);
    }
  }

  async toggleFavoriteDoctor(): Promise<void> {
    if (!this.doctor) {
      return;
    }

    this.isFavoriteDoctor = !this.isFavoriteDoctor;

    await (this.isFavoriteDoctor
      ? this.patientService.addDoctorToFavourites(this.patient.id, this.doctor.id)
      : this.patientService.removeDoctorFromFavourites(this.patient.id, this.doctor.id));
  }

  goToMessage(message: string): void {
    this.messageNavigationService.goToMessage(message, { preserveQueryParams: true });
  }

  getStarsForQuestion(questionId: string): number[] {
    return this.stars[questionId] || [];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
